import { Fragment, type ReactNode } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { AppWindow, Boxes, Briefcase, Building2, FileText, Folder, Plus } from "lucide-react";
import type { ContextNode } from "@perlab/iframe-bridge";
import { canonicalSubAppId, getSubApp, ZARVIS_APP_ID } from "@perlab/registry";
import { nodeAncestry, resolveChain, useContextChain } from "../state/ContextChain";
import { resolveZarvisIdentity } from "../inspector/zarvisIdentity";
import { BreadcrumbSegment, type BreadcrumbOption } from "./BreadcrumbSegment";
import { navTo } from "./navTargets";
import { AppIconResolver } from "./AppIconResolver";

const LEVEL_TITLES = ["Switch artifact", "Switch sub-artifact", "Switch nested"];
// Placeholder labels for unselected next-level slots.
const NEXT_ARTIFACT_LABEL = ["Artifact", "Sub-artifact", "Deeper artifact"];

// Smart-collapse thresholds. Keep HEAD_COUNT segments on the left and
// TAIL_COUNT on the right; collapse the middle to a "…" ellipsis segment
// whose dropdown lists each hidden level with its type icon + type label.
const HEAD_COUNT = 2;
const TAIL_COUNT = 2;
const MAX_VISIBLE = HEAD_COUNT + TAIL_COUNT;

type LevelType = "workspace" | "client" | "engagement" | "project" | "app" | "artifact" | "next";

const LEVEL_TYPE_LABEL: Record<LevelType, string> = {
  workspace: "Workspace",
  client: "Client",
  engagement: "Engagement",
  project: "Project",
  app: "App",
  artifact: "Artifact",
  next: "Open",
};

function levelTypeIcon(type: LevelType): ReactNode {
  switch (type) {
    case "workspace": return <Boxes size={14} aria-hidden />;
    case "client": return <Building2 size={14} aria-hidden />;
    case "engagement": return <Briefcase size={14} aria-hidden />;
    case "project": return <Folder size={14} aria-hidden />;
    case "app": return <AppWindow size={14} aria-hidden />;
    case "artifact": return <FileText size={14} aria-hidden />;
    case "next": return <Plus size={14} aria-hidden />;
  }
}

// A chain entry — render data plus the metadata the ellipsis menu needs to
// surface it as a jump target when collapsed.
interface ChainEntry {
  key: string;
  navLabel: string;
  navIcon?: ReactNode;
  levelType: LevelType;
  // Deepest non-placeholder entry is "current"; render() receives the flag.
  render: (isCurrent: boolean) => ReactNode;
  // Used by the ellipsis menu to fire the same nav action as a direct click.
  onNav: () => void;
  isPlaceholder?: boolean;
}

function Separator() {
  return (
    <span aria-hidden className="text-[var(--color-text-disabled)] select-none px-0.5">
      ›
    </span>
  );
}

// TopBar chain — Option C (hover-reveal chevron breadcrumb) with URL-driven
// truncation. Each level appears as a breadcrumb segment only when the URL
// actually contains it; otherwise that slot is a "+ {LevelName}" placeholder.
//   Text click  → navigate to that level's /home URL (truncates deeper).
//   Chevron     → switcher dropdown (visible on hover).
//   Placeholder → muted "+ {LevelName}" for the next unselected level.
export function ContextChain() {
  const navigate = useNavigate();
  // The CURRENT node = the route's $node (generic /n/$node route) — NOT scalar precedence. For a FREE
  // placement (client under an engagement) the scalars resolve to the host ancestor, not the node you're
  // on, so the "current" highlight would land on the wrong crumb (3-lens audit C1, 2026-07-03).
  const { node: routeNodeParam } = useParams({ strict: false }) as { node?: string };
  const {
    workspaces = [],
    projects,
    workspaceId,
    clientId,
    engagementId,
    projectId,
    appId,
    appTreeByAppId,
    pathSegments,
    scopeNodes,
    trailNodeId,
    trailAppId,
  } = useContextChain();


  // --- Scope-depth flags. ONE source of truth (B1): both the breadcrumb and the sidebar derive
  // from the canonical scalars (clientId/engagementId/projectId/appId), which syncScope mirrors from
  // the URL (an absent level resets to ""). A level appears as a breadcrumb only when its scalar is
  // backed; otherwise it appears as the next-slot placeholder. The breadcrumb can no longer show a
  // level the URL/sidebar doesn't — the two spines cannot desync. ---
  const urlHasClient = !!clientId;
  const urlHasEng = !!engagementId;
  const urlHasProject = !!projectId;
  const urlHasApp = !!appId;

  // `project` + `app` drive artifact nav below. The breadcrumb scope crumbs are built from the node-graph
  // TRAIL further down (not these scalars). The APP crumb is built from the persistent trail app
  // (`trailApp`) so it survives clicking an ancestor scope crumb — re-enterable at the trail node.
  const project = projects.find((p) => p.id === projectId);
  const canonicalAppId = appId ? canonicalSubAppId(appId) : undefined;
  const canonicalTrailAppId = trailAppId ? canonicalSubAppId(trailAppId) : undefined;
  const app = canonicalAppId ? getSubApp(canonicalAppId) : undefined;
  const trailApp = canonicalTrailAppId ? getSubApp(canonicalTrailAppId) : undefined;
  const appTree = canonicalAppId ? appTreeByAppId[canonicalAppId] : undefined;
  const workspaceLabel = workspaces.find((workspace) => workspace.id === workspaceId)?.label;
  const zarvisIdentity = resolveZarvisIdentity(workspaceLabel);
  const trailAppIdentity = trailApp?.id === ZARVIS_APP_ID ? zarvisIdentity : null;
  const trailAppLabel = trailAppIdentity?.displayName ?? trailApp?.name;
  const trailAppIcon = (): ReactNode => trailAppIdentity ? (
    <span className="grid size-4 place-items-center overflow-hidden rounded-full border border-[var(--color-accent)]/60 bg-[var(--color-accent)]/[0.12]">
      <img
        src={trailAppIdentity.avatarUrl}
        alt=""
        data-testid="zarvis-breadcrumb-avatar"
        className="size-full object-cover"
      />
    </span>
  ) : <AppIconResolver name={trailApp?.icon} />;

  // Artifact tree only meaningful when URL is inside an app.
  const chainLevels = urlHasApp && appTree ? resolveChain(appTree, pathSegments) : [];
  const nextChildren: ContextNode[] = urlHasApp && appTree
    ? chainLevels.length === 0
      ? appTree.children ?? []
      : chainLevels[chainLevels.length - 1]!.node.children ?? []
    : [];

  // ----- Artifact switcher actions (scope + app crumbs are click-only; they navigate via goNode/navToTrailApp). -----
  // JUSTIFIED: artifact-segment nav targets the app root because artifact-path routing
  // is a later phase (SubAppPage real mount, Phase 1/2) — artifact levels only render
  // once a guest app pushes a tree, so this handler is inert until then by design.
  const goToSegment = (_level: number, _segId: string) => {
    if (!app || !projectId) return;
    navigate(navTo.app(workspaceId, clientId, engagementId, projectId, app.id));
  };

  // The persisted app crumb re-enters the TRAIL app at the TRAIL's deepest node (navTo.nodeApp) — so
  // clicking it after you've navigated up returns you to where the app was mounted, not the shallow URL.
  const navToTrailApp = () => { if (canonicalTrailAppId && trailNodeId) navigate(navTo.nodeApp(workspaceId, trailNodeId, canonicalTrailAppId)); };
  const navToSegment = (_level: number) => {
    if (!app || !project) return;
    navigate(navTo.app(workspaceId, clientId, engagementId, project.id, app.id));
  };

  // Crumbs are CLICK-ONLY (operator 2026-07-03): creation + lateral switch flow through the ONE "+ Add"
  // door (AddSlot); rename/delete live on the content Overview page (NodeContent). No per-crumb menu.

  // ----- Build chain (node-graph trail + app/artifact) -----
  const chain: ChainEntry[] = [];

  // NOTE: the WORKSPACE level is intentionally NOT in the breadcrumb — the fixed workspace
  // identity lives in the sidebar header. The breadcrumb starts at
  // Client to avoid duplicating the workspace (de-dup decision 2026-06-23).

  // ----- Node-graph TRAIL crumbs (operator 2026-07-03) -----
  // The breadcrumb renders the PERSISTENT TRAIL's true ancestry (scopeNodes parent-walk), not the
  // scalar levels — so free placements render in real path order AND clicking an ancestor crumb keeps
  // the deeper trail (trailNodeId persists, so you can re-descend). The ACTIVE crumb is the CURRENT URL
  // node (deepest scalar); trail crumbs deeper than it stay visible. Each crumb: click → navigate to
  // that node; chevron → switch to a REAL sibling (same parent + type, node-graph — fixes the
  // client-less-engagement-shows-only-itself bug). Workspace/firm are omitted (they live in the sidebar).
  const currentUrlNodeId = routeNodeParam || projectId || engagementId || clientId || workspaceId;
  const nodeById = new Map(scopeNodes.map((n) => [n.id, n]));
  const trailSelf = trailNodeId ? nodeById.get(trailNodeId) : undefined;
  const trailPath = trailSelf
    ? [...nodeAncestry(trailNodeId, scopeNodes)].reverse().map((a) => nodeById.get(a.id)!).concat(trailSelf)
    : [];
  const CRUMB_ICON: Record<string, ReactNode> = {
    client: <Building2 size={14} aria-hidden />,
    engagement: <Briefcase size={14} aria-hidden />,
    project: <Folder size={14} aria-hidden />,
  };
  for (const node of trailPath) {
    if (node.node_type !== "client" && node.node_type !== "engagement" && node.node_type !== "project") continue;
    const nType = node.node_type;
    const goNode = (id: string): void => { navigate(navTo.nodeHome(workspaceId, id)); };
    // CLICK-ONLY crumb (operator 2026-07-03): no switcher dropdown. Lateral SWITCH (siblings) moved into
    // the "+ Add" door; RENAME/DELETE live on the content Overview page. Text click just navigates.
    chain.push({
      key: node.id,
      navLabel: node.name,
      navIcon: CRUMB_ICON[nType],
      levelType: nType,
      onNav: () => goNode(node.id),
      render: () => (
        <BreadcrumbSegment
          icon={CRUMB_ICON[nType]}
          typeLabel={LEVEL_TYPE_LABEL[nType]}
          label={node.name}
          isCurrent={!appId && node.id === currentUrlNodeId}
          onNavigate={() => goNode(node.id)}
        />
      ),
    });
  }

  // App affordance — at the current scope level (V2). The ACTIVE app's chip ALWAYS shows,
  // regardless of client (a workspace-mounted app like Library, or an app on a client-less
  // cross-client host, previously rendered NO app crumb — the `if (currentClient)` gate was the
  // second half of the 2026-07-02 breadcrumb bug). The "+App" PLACEHOLDER is LEVEL-aware, not
  // client-gated (48.23 matrix fix): it shows wherever mountable apps exist at the current level
  // — host engagements/projects included (the old `currentClient` gate silently dropped it there,
  // the operator-reported "+App is gone" on /e/x-sector-benchmark/p/benchmark-wave-1).
  // The app crumb is built from the TRAIL app (persisted) — so it survives clicking an ancestor scope
  // crumb (shows greyed, click to re-enter at the trail node) and only drops on a new branch (operator
  // 2026-07-03: "App disappeared when I clicked the client crumb"). Current only when you're really in it.
  {
    if (trailApp) {
      chain.push({
        key: "app",
        navLabel: trailAppLabel ?? trailApp.name,
        navIcon: trailAppIcon(),
        levelType: "app",
        onNav: navToTrailApp,
        render: (isCurrent) => (
          <BreadcrumbSegment
            icon={trailAppIcon()}
            typeLabel={LEVEL_TYPE_LABEL.app}
            label={trailAppLabel ?? trailApp.name}
            isCurrent={isCurrent}
            onNavigate={navToTrailApp}
          />
        ),
      });
    }
  }

  // Artifact levels + next-level placeholder — only meaningful when in an app.
  if (urlHasApp && app) {
    chainLevels.forEach((rl) => {
      chain.push({
        key: `artifact-${rl.level}-${rl.node.id}`,
        navLabel: rl.node.label,
        navIcon: rl.node.icon ? <AppIconResolver name={rl.node.icon} /> : undefined,
        levelType: "artifact",
        onNav: () => navToSegment(rl.level),
        render: (isCurrent) => (
          <BreadcrumbSegment
            icon={rl.node.icon ? <AppIconResolver name={rl.node.icon} /> : undefined}
            label={rl.node.label}
            isCurrent={isCurrent}
            options={rl.siblings.map((s) => ({
              id: s.id,
              label: s.label,
              iconNode: s.icon ? <AppIconResolver name={s.icon} /> : undefined,
            }))}
            activeId={rl.node.id}
            onSelect={(id) => goToSegment(rl.level, id)}
            onNavigate={() => navToSegment(rl.level)}
            menuTitle={LEVEL_TITLES[Math.min(rl.level, LEVEL_TITLES.length - 1)]}
            searchable
            searchPlaceholder="Search…"
          />
        ),
      });
    });

    if (nextChildren.length > 0) {
      const nextLabel = NEXT_ARTIFACT_LABEL[Math.min(chainLevels.length, NEXT_ARTIFACT_LABEL.length - 1)]!;
      chain.push({
        key: "artifact-placeholder",
        navLabel: nextLabel,
        levelType: "next",
        isPlaceholder: true,
        onNav: () => {},
        render: () => (
          <BreadcrumbSegment
            variant="placeholder"
            label={nextLabel}
            options={nextChildren.map((n) => ({
              id: n.id,
              label: n.label,
              iconNode: n.icon ? <AppIconResolver name={n.icon} /> : undefined,
            }))}
            onSelect={(id) => goToSegment(chainLevels.length, id)}
            menuTitle={LEVEL_TITLES[Math.min(chainLevels.length, LEVEL_TITLES.length - 1)]}
            searchable
            searchPlaceholder="Search…"
          />
        ),
      });
    }
  }

  // "Current" = the deepest scalar-backed level (where the page actually is). Since the breadcrumb
  // shape now equals the URL scope, the deepest non-placeholder IS the current level (no phantom
  // deeper-than-URL segments). In an app, the deepest non-placeholder (app/artifact) wins.
  let currentIdx = -1;
  if (urlHasApp) {
    for (let i = chain.length - 1; i >= 0; i--) {
      if (!chain[i]!.isPlaceholder) { currentIdx = i; break; }
    }
  } else {
    const urlDeepestType = urlHasProject ? "project" : urlHasEng ? "engagement" : urlHasClient ? "client" : null;
    if (urlDeepestType) {
      for (let i = chain.length - 1; i >= 0; i--) {
        if (!chain[i]!.isPlaceholder && chain[i]!.levelType === urlDeepestType) { currentIdx = i; break; }
      }
    }
  }

  // Smart collapse: head + ellipsis + tail. Current is in the tail.
  const shouldCollapse = chain.length > MAX_VISIBLE;
  const visibleHead = shouldCollapse ? chain.slice(0, HEAD_COUNT) : chain;
  const visibleTail = shouldCollapse ? chain.slice(-TAIL_COUNT) : [];
  const hidden = shouldCollapse ? chain.slice(HEAD_COUNT, chain.length - TAIL_COUNT) : [];

  const ellipsisOptions: BreadcrumbOption[] = hidden.map((c) => ({
    id: c.key,
    label: c.navLabel,
    iconNode: c.navIcon ?? levelTypeIcon(c.levelType),
    hint: LEVEL_TYPE_LABEL[c.levelType],
  }));

  const onEllipsisSelect = (id: string) => {
    const item = hidden.find((c) => c.key === id);
    item?.onNav();
  };

  return (
    <nav
      aria-label="Context"
      className="flex items-center gap-1.5 flex-nowrap min-w-0 overflow-x-auto"
    >
      {visibleHead.map((item, i) => (
        <Fragment key={item.key}>
          {i > 0 && <Separator />}
          {item.render(i === currentIdx)}
        </Fragment>
      ))}

      {shouldCollapse && (
        <Fragment>
          <Separator />
          <BreadcrumbSegment
            label="…"
            options={ellipsisOptions}
            onSelect={onEllipsisSelect}
            menuTitle={`Hidden levels (${hidden.length})`}
          />
        </Fragment>
      )}

      {visibleTail.map((item, i) => {
        const globalIdx = chain.length - visibleTail.length + i;
        return (
          <Fragment key={item.key}>
            <Separator />
            {item.render(globalIdx === currentIdx)}
          </Fragment>
        );
      })}
    </nav>
  );
}

// Typed nav targets + URL helpers moved to ./navTargets (48.23 size split — pure module, no
// React). Re-exported here so every existing `from "./ContextChain"` import keeps working.
export { navTo, buildAppUrl, parseSegmentsFromPath } from "./navTargets";
